/**
 * The waveform drawn behind the scrubber.
 *
 * It is decorative: audio is only synthesised when someone presses play, so before
 * that there is nothing to measure. Instead the shape is generated from a seed (the
 * article's path), which gives every article its own waveform and the same one on
 * every visit.
 *
 * It is used as a CSS mask on the existing progress bar, so the bar's own background
 * shows through as the unplayed bars and its fill as the played ones. The scrubbing,
 * dragging and keyboard code never needs to know it is there.
 */

/** FNV-1a: a small, well-spread 32-bit string hash. */
function hash(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32: a seeded PRNG returning floats in [0, 1). */
function prng(seed: number): () => number {
  let a = seed;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Bar heights in [0.2, 1], shaped like speech: phrases, syllables, short pauses. */
export function waveformBars(seed: string, bars = 56): number[] {
  const rand = prng(hash(seed));
  const raw: number[] = [];
  let phrase = 0.6 + rand() * 0.4;

  for (let i = 0; i < bars; i++) {
    if (rand() < 0.14) phrase = 0.45 + rand() * 0.55;
    let v = phrase * (0.4 + rand() * 0.6);
    if (rand() < 0.07) v *= 0.3;
    raw.push(v);
  }

  // A light smoothing pass so neighbouring bars relate, the way syllables do.
  return raw.map((v, i) => {
    const prev = raw[i - 1] ?? v;
    const next = raw[i + 1] ?? v;
    return Math.max(0.2, Math.min(1, (prev + 2 * v + next) / 4));
  });
}

/** The waveform as a CSS `url()` holding an SVG, for use as a mask image. */
export function waveformMask(seed: string, bars = 56): string {
  const slot = 4;
  const height = 32;
  const rects = waveformBars(seed, bars)
    .map((v, i) => {
      const h = Math.round(v * height * 10) / 10;
      const y = Math.round(((height - h) / 2) * 10) / 10;
      return `<rect x="${i * slot + 0.75}" y="${y}" width="2.5" height="${h}" rx="1.25"/>`;
    })
    .join("");
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${bars * slot} ${height}" preserveAspectRatio="none">${rects}</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
